import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarUI } from './ui/calendar';
import { Calendar as CalendarIcon, CheckCircle, XCircle, Clock, DollarSign, FileText, CalendarDays } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { useCalendarHolidays } from '../hooks/useHolidays';

const QuotationApprovalDialog = ({ open, onOpenChange, quotation, projectId, onApprovalComplete, showFullDetails = true }) => {
    const { holidayDates, isHoliday, onMonthChange: onHolidayMonthChange } = useCalendarHolidays();
    const [approvalType, setApprovalType] = useState('approve'); // 'approve', 'approve-reschedule', 'reject'
    const [rescheduleDate, setRescheduleDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [slotsAvailability, setSlotsAvailability] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(new Date());
    const [monthAvailability, setMonthAvailability] = useState({
        availableDates: [],
        unavailableDates: []
    });
    const [loadingMonthAvailability, setLoadingMonthAvailability] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [extraWorkNotes, setExtraWorkNotes] = useState('');

    const renderDatePicker = () => (
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen} modal={true}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCalendarOpen((v) => !v); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="w-full justify-start text-left font-normal h-11 mt-1"
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {rescheduleDate
                        ? format(rescheduleDate, 'PPP')
                        : 'Select proposed start date'
                    }
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-auto p-0 z-[60]"
                align="start"
                side="bottom"
                sideOffset={4}
                avoidCollisions={true}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
            >
                <CalendarUI
                    mode="single"
                    selected={rescheduleDate}
                    onSelect={(date) => {
                        setRescheduleDate(date);
                        setCalendarOpen(false);
                    }}
                    disabled={(date) => date < new Date() || isHoliday(date)}
                    month={calendarMonth}
                    onMonthChange={(month) => { setCalendarMonth(month); onHolidayMonthChange(month); }}
                    modifiers={{
                        available: monthAvailability.availableDates,
                        unavailable: monthAvailability.unavailableDates,
                        holiday: holidayDates
                    }}
                    modifiersClassNames={{
                        available: 'bg-green-100 text-green-900 hover:bg-green-200',
                        unavailable: 'bg-red-100 text-red-900 hover:bg-red-200',
                        holiday: 'holiday-day'
                    }}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );

    const renderSlotGrid = () => (
        loadingSlots ? (
            <p className="text-sm text-gray-500">Loading slots...</p>
        ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                {slotsAvailability.map((slot) => (
                    <Button
                        key={slot.slotId}
                        variant="outline"
                        className={`flex-col h-auto py-3 items-center ${selectedSlot?.slotId === slot.slotId
                            ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                            : slot.isAvailable
                                ? 'border-green-400 hover:bg-green-50'
                                : 'border-red-300 bg-red-50 opacity-60 cursor-not-allowed'
                            }`}
                        onClick={() => slot.isAvailable && setSelectedSlot(slot)}
                        disabled={!slot.isAvailable}
                    >
                        <span className="font-semibold text-sm text-center">{slot.slotName.split('(')[0].trim()}</span>
                        <span className="text-xs mt-0.5" style={{ color: selectedSlot?.slotId === slot.slotId ? 'rgba(255,255,255,0.8)' : 'rgb(75,85,99)' }}>({slot.slotName.match(/\(([^)]+)\)/)?.[1] || `${slot.startTime} - ${slot.endTime}`})</span>
                        <Badge className={`mt-1 text-xs ${slot.isAvailable ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-red-500 hover:bg-red-500 text-white'}`}>
                            {slot.isAvailable ? 'Available' : 'Fully Booked'}
                        </Badge>
                    </Button>
                ))}
            </div>
        )
    );

    useEffect(() => {
        if (open && quotation) {
            setApprovalType('approve');
            setRescheduleDate(null);
            setSelectedSlot(null);
            setRejectionReason('');
            setCalendarMonth(new Date());
            fetchDocuments();
            fetchExtraWorkNotes();
        }
    }, [open, quotation]);

    useEffect(() => {
        if (approvalType === 'approve-reschedule' && rescheduleDate) {
            const dateStr = format(rescheduleDate, 'yyyy-MM-dd');
            fetchSlotAvailability(dateStr);
        }
    }, [rescheduleDate, approvalType]);

    useEffect(() => {
        if (approvalType === 'approve-reschedule' && calendarMonth) {
            fetchMonthAvailability(calendarMonth);
        }
    }, [calendarMonth, approvalType]);

    const fetchExtraWorkNotes = async () => {
        if (!projectId) return;
        try {
            const project = await api.get(`/api/projects/${projectId}`);
            setExtraWorkNotes(project.extraWorkNotes || '');
        } catch (error) {
            console.error('Error fetching project extra work notes:', error);
        }
    };

    const fetchDocuments = async () => {
        if (!quotation || !projectId) return;

        try {
            setLoadingDocuments(true);
            console.log('Fetching documents for quotation:', quotation.id, 'project:', projectId);
            const response = await api.get(`/api/projects/${projectId}/documents`, {
                params: {
                    referenceType: 'quotation',
                    referenceId: quotation.id
                }
            });
            console.log('Documents API response:', response);
            // Handle both response.documents and direct array response
            const docs = response.documents || response || [];
            console.log('Parsed documents:', docs);
            setDocuments(Array.isArray(docs) ? docs : []);
        } catch (error) {
            console.error('Error fetching quotation documents:', error);
            setDocuments([]);
        } finally {
            setLoadingDocuments(false);
        }
    };

    const fetchMonthAvailability = async (month) => {
        try {
            setLoadingMonthAvailability(true);

            const year = month.getFullYear();
            const monthNum = month.getMonth() + 1;

            const data = await api.getMonthAvailability(year, monthNum);

            const available = [];
            const unavailable = [];

            if (data.dates) {
                data.dates.forEach(dateInfo => {
                    const date = new Date(dateInfo.date);
                    if (dateInfo.hasAvailability) {
                        available.push(date);
                    } else {
                        unavailable.push(date);
                    }
                });
            }

            setMonthAvailability({ availableDates: available, unavailableDates: unavailable });
        } catch (error) {
            console.error('Error fetching month availability:', error);
            toast.error('Failed to load calendar availability');
        } finally {
            setLoadingMonthAvailability(false);
        }
    };

    const fetchSlotAvailability = async (date) => {
        try {
            setLoadingSlots(true);
            const data = await api.getSlotAvailability(date);
            setSlotsAvailability(data.slots || []);
        } catch (error) {
            console.error('Error fetching slot availability:', error);
            toast.error('Failed to load slot availability');
            setSlotsAvailability([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleSubmit = async () => {
        if (approvalType === 'reject' && !rejectionReason.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        if (approvalType === 'approve-reschedule' && (!rescheduleDate || !selectedSlot)) {
            toast.error('Please select a date and time slot for rescheduling');
            return;
        }

        try {
            setIsSubmitting(true);

            const requestData = {
                quotationId: quotation.id,
                projectId: projectId,
                approved: approvalType !== 'reject',
                rejectionReason: approvalType === 'reject' ? rejectionReason : null,
                rescheduleSv1: approvalType === 'approve-reschedule',
                newSv1Date: approvalType === 'approve-reschedule' && rescheduleDate ? format(rescheduleDate, 'yyyy-MM-dd') : null,
                newSlotId: approvalType === 'approve-reschedule' ? selectedSlot.slotId : null
            };

            await api.post(`/api/projects/quotations/${quotation.id}/approve`, requestData);

            toast.success(
                approvalType === 'reject'
                    ? 'Quotation rejected successfully'
                    : 'Quotation approved successfully'
            );

            onOpenChange(false);
            if (onApprovalComplete) {
                onApprovalComplete();
            }
        } catch (error) {
            console.error('Error processing quotation approval:', error);
            toast.error(error.response?.data?.message || 'Failed to process quotation approval');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!quotation) return null;

    const formatCurrency = (amount) => {
        return `₹${amount?.toLocaleString('en-IN') || 0}`;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[90vw] sm:max-w-3xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
                <DialogHeader className="pr-8">
                    <DialogTitle className="flex items-center gap-2 pr-4">
                        <FileText className="h-5 w-5 shrink-0" />
                        <span className="break-all">Quotation Approval - {quotation.quotationNumber}</span>
                    </DialogTitle>
                    <DialogDescription>
                        Review and approve or reject the quotation
                    </DialogDescription>
                </DialogHeader>

                {showFullDetails ? (
                    <div className="space-y-6">
                        {/* Header Information */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-sm text-gray-600">Quotation Number</Label>
                                <p className="font-semibold">{quotation.quotationNumber}</p>
                            </div>
                            <div>
                                <Label className="text-sm text-gray-600">Date</Label>
                                <p className="font-semibold">
                                    {quotation.createdAt ? new Date(quotation.createdAt).toLocaleDateString() : format(new Date(), 'MMM dd, yyyy')}
                                </p>
                            </div>
                            <div>
                                <Label className="text-sm text-gray-600">Type</Label>
                                <div className="mt-1">
                                    <Badge variant={quotation.quotationType === 'base_work' ? 'default' : 'secondary'}>
                                        {quotation.quotationType === 'base_work' ? 'Base Work' : 'Additional Work'}
                                    </Badge>
                                </div>
                            </div>
                            <div>
                                <Label className="text-sm text-gray-600">Status</Label>
                                <div className="mt-1">
                                    <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
                                        Pending Approval
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        <Separator />

                        {/* Entity-Wise Breakdown (if available) */}
                        {quotation.quotationItems && Array.isArray(quotation.quotationItems) && quotation.quotationItems.length > 0 && (
                            <>
                                <Separator />
                                <div className="space-y-3">
                                    <Label className="text-base font-semibold">Entity-Wise Pricing Breakdown</Label>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm border border-gray-200">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="p-2 border text-left">Entity</th>
                                                    <th className="p-2 border text-right">Unit Price (₹)</th>
                                                    <th className="p-2 border text-right">Sqft</th>
                                                    <th className="p-2 border text-right">Total (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {quotation.quotationItems.map((item, idx) => (
                                                    <tr key={idx} className="border-b hover:bg-gray-50">
                                                        <td className="p-2 border font-medium">{item.entityName || item.EntityName || 'Entity'}</td>
                                                        <td className="p-2 border text-right">{(item.unitPrice ?? item.UnitPrice ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                                        <td className="p-2 border text-right">{(item.sqft ?? item.Sqft ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                                        <td className="p-2 border text-right font-semibold">₹{(item.totalPrice ?? item.TotalPrice ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <Separator />
                            </>
                        )}

                        {/* Amount Breakdown */}
                        <div className="space-y-3">
                            <Label className="text-base font-semibold">Amount Breakdown</Label>
                            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Base Work Amount:</span>
                                    <span className="font-semibold">₹{(quotation.baseWorkAmount || 0).toLocaleString()}</span>
                                </div>
                                {quotation.extraWorkAmount > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Extra Work Amount:</span>
                                        <span className="font-semibold">₹{quotation.extraWorkAmount.toLocaleString()}</span>
                                    </div>
                                )}
                                {quotation.discountAmount > 0 && (
                                    <div className="flex justify-between text-red-600">
                                        <span>Discount ({quotation.discountType === 'percentage' ? `${quotation.discountValue}%` : `₹${quotation.discountValue}`}):</span>
                                        <span className="font-semibold">-₹{quotation.discountAmount.toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Subtotal:</span>
                                    <span className="font-semibold">₹{(quotation.taxableAmount || quotation.totalAmount || 0).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">GST ({quotation.gstPercentage}%):</span>
                                    <span className="font-semibold">₹{quotation.taxAmount?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                                </div>
                                <Separator />
                                <div className="flex justify-between text-lg">
                                    <span className="font-semibold">Grand Total:</span>
                                    <span className="font-bold text-teal-600">₹{quotation.grandTotal?.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>

                        {quotation.notes && (
                            <>
                                <Separator />
                                <div className="space-y-2">
                                    <Label className="text-base font-semibold">Notes / Remarks</Label>
                                    <p className="text-gray-700 bg-gray-50 p-3 rounded">{quotation.notes}</p>
                                </div>
                            </>
                        )}

                        {extraWorkNotes && (
                            <>
                                <Separator />
                                <div className="space-y-2">
                                    <Label className="text-base font-semibold">Extra Work Notes</Label>
                                    <p className="text-gray-700 bg-orange-50 p-3 rounded border border-orange-200">{extraWorkNotes}</p>
                                </div>
                            </>
                        )}

                        {/* Documents Section */}
                        {(loadingDocuments || documents.length > 0) && (
                            <>
                                <Separator />
                                <div className="space-y-3">
                                    <Label className="text-base font-semibold flex items-center gap-2">
                                        <FileText className="w-4 h-4" />
                                        Documents ({documents.length})
                                    </Label>
                                    {loadingDocuments ? (
                                        <p className="text-sm text-gray-500">Loading documents...</p>
                                    ) : documents.length > 0 ? (
                                        <div className="grid grid-cols-1 gap-2">
                                            {documents.map((doc) => (
                                                <a
                                                    key={doc.id}
                                                    href={doc.fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 transition-colors"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="w-4 h-4 text-gray-500" />
                                                        <span className="text-sm font-medium text-gray-700 truncate">
                                                            {doc.fileName || doc.documentName}
                                                        </span>
                                                    </div>
                                                    <Badge variant="outline" className="text-xs">
                                                        View
                                                    </Badge>
                                                </a>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            </>
                        )}

                        <Separator />

                        {/* SV1 Schedule Info - only for base work */}
                        {quotation.quotationType !== 'additional_work' && (
                            <>
                                <div className="space-y-3">
                                    <Label className="text-base font-semibold flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        Site Visit 1 Schedule
                                    </Label>
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-sm text-gray-600">Proposed Date</Label>
                                                <p className="font-semibold">
                                                    {quotation.proposedStartDate ? format(new Date(quotation.proposedStartDate), 'MMM dd, yyyy') : 'Not specified'}
                                                </p>
                                            </div>
                                            <div>
                                                <Label className="text-sm text-gray-600">Slot Timing</Label>
                                                <p className="font-semibold">{quotation.slotName || 'Not specified'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <Separator />
                            </>
                        )}

                        {/* Approval Options */}
                        <div className="space-y-4">
                            <Label className="text-base font-semibold">Approval Decision</Label>
                            <div className="space-y-3">
                                <Button
                                    variant={approvalType === 'approve' ? 'default' : 'outline'}
                                    className={`w-full justify-start ${approvalType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                    onClick={() => setApprovalType('approve')}
                                >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Approve Quotation
                                </Button>

                                {quotation.quotationType !== 'additional_work' && (
                                    <Button
                                        variant={approvalType === 'approve-reschedule' ? 'default' : 'outline'}
                                        className={`w-full justify-start ${approvalType === 'approve-reschedule' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                                        onClick={() => setApprovalType('approve-reschedule')}
                                    >
                                        <CalendarDays className="w-4 h-4 mr-2" />
                                        Approve with SV1 Reschedule
                                    </Button>
                                )}

                                <Button
                                    variant={approvalType === 'reject' ? 'default' : 'outline'}
                                    className={`w-full justify-start ${approvalType === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                                    onClick={() => setApprovalType('reject')}
                                >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reject Quotation
                                </Button>
                            </div>

                            {/* Reschedule Section */}
                            {approvalType === 'approve-reschedule' && (
                                <div className="border rounded-lg p-4 space-y-4 bg-blue-50">
                                    <h3 className="font-semibold text-sm">Reschedule Site Visit 1</h3>

                                    <div>
                                        <Label>Select Date (Green = Available, Red = Fully Booked)</Label>
                                        {renderDatePicker()}
                                    </div>

                                    {rescheduleDate && (
                                        <div>
                                            <Label>Select Time Slot</Label>
                                            {renderSlotGrid()}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Rejection Reason */}
                            {approvalType === 'reject' && (
                                <div className="border rounded-lg p-4 space-y-3 bg-red-50">
                                    <Label>Reason for Rejection *</Label>
                                    <Textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Please provide reason for rejecting this quotation..."
                                        rows={4}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6 py-4">
                        {/* SV1 Schedule Info - only for base work */}
                        {quotation.quotationType !== 'additional_work' && (
                            <div className="border rounded-lg p-4 bg-gray-50">
                                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    Current SV1 Schedule
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs text-gray-500">Proposed Date</Label>
                                        <p className="text-sm font-medium">
                                            {quotation.proposedStartDate ? format(new Date(quotation.proposedStartDate), 'MMM dd, yyyy') : 'Not specified'}
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-gray-500">Slot Timing</Label>
                                        <p className="text-sm font-medium">
                                            {quotation.slotName || 'Not specified'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Approval Options */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-sm">Approval Decision</h3>
                            <div className="space-y-3">
                                <Button
                                    variant={approvalType === 'approve' ? 'default' : 'outline'}
                                    className={`w-full justify-start ${approvalType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                    onClick={() => setApprovalType('approve')}
                                >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Approve Quotation
                                </Button>

                                {quotation.quotationType !== 'additional_work' && (
                                    <Button
                                        variant={approvalType === 'approve-reschedule' ? 'default' : 'outline'}
                                        className={`w-full justify-start ${approvalType === 'approve-reschedule' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                                        onClick={() => setApprovalType('approve-reschedule')}
                                    >
                                        <CalendarDays className="w-4 h-4 mr-2" />
                                        Approve with SV1 Reschedule
                                    </Button>
                                )}

                                <Button
                                    variant={approvalType === 'reject' ? 'default' : 'outline'}
                                    className={`w-full justify-start ${approvalType === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                                    onClick={() => setApprovalType('reject')}
                                >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reject Quotation
                                </Button>
                            </div>

                            {/* Reschedule Section */}
                            {approvalType === 'approve-reschedule' && (
                                <div className="border rounded-lg p-4 space-y-4 bg-blue-50">
                                    <h3 className="font-semibold text-sm">Reschedule Site Visit 1</h3>

                                    <div>
                                        <Label>Select Date (Green = Available, Red = Fully Booked)</Label>
                                        {renderDatePicker()}
                                    </div>

                                    {rescheduleDate && (
                                        <div>
                                            <Label>Select Time Slot</Label>
                                            {renderSlotGrid()}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Rejection Reason */}
                            {approvalType === 'reject' && (
                                <div className="border rounded-lg p-4 space-y-3 bg-red-50">
                                    <Label>Reason for Rejection *</Label>
                                    <Textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Please provide reason for rejecting this quotation..."
                                        rows={4}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={
                            approvalType === 'reject'
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-green-600 hover:bg-green-700'
                        }
                    >
                        {isSubmitting ? 'Processing...' : (
                            approvalType === 'reject' ? 'Reject Quotation' : 'Approve Quotation'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default QuotationApprovalDialog;
